import Plotly, { Dash, Data, PlotData } from "plotly.js";
import { APIResponse } from "services/HttpService";
import { Instant } from "@js-joda/core";
import { OptionalFilter } from "components/ComponentTypeTypes";
import { ReportOptions } from "models/component-print-models";
import { DashboardComponentPropsBase, IComponentTitled } from "components/pages/dashboard/components/interfaces";
import { DweenTimeUnit } from "utils/time-utils";
import { JSX } from "react";
import { ChartType } from "../dataset-window/models/types";
import { IDateRange } from "../product-template-table/utils/interfaces";

export enum LineEditorLoadingTypes {
  TIME = "Time",
  PRODUCT_ENTRY = "Product Entry",
}
export interface DatasetChartWrapperProps<ResT = any> extends DatasetChartProps, DashboardComponentPropsBase<ReportOptions> {
  datasetChart?: "pie" | "scatter" | "bar";
  uniqueKey: string;
  children?: (props: DatasetChartChildrenProps) => JSX.Element;
  promiseHandler: (res: APIResponse<ResT>) => PlotDataEx[];
  axesConfig?: (data: Partial<PlotDataEx>[]) => AxesConfig;
  chartConfig?: Partial<Plotly.Config>;
  maxDTicks?: number;
  dticks?: number;
  setDticks?: (value: number | undefined) => void;
  chartInitialized?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
  dateAndRefreshButtonsVisible?: boolean;
  productItemsDateRange?: Array<string>;
  getMostRecent?: boolean;
  statusHandler?: (status: string) => void;
  withoutIdentifiers?: boolean;
}

export interface DatasetChartChildrenProps extends DashboardComponentPropsBase<ReportOptions> {
  width: number;
  height: number;
  axesConfig?: AxesConfig;
  data: Array<any>;
  chartConfig?: Partial<Plotly.Config>;
  chartDialogOpen?: boolean;
}

export interface DatasetChartProps extends IComponentTitled, DashboardComponentPropsBase<ReportOptions> {
  identifiers?: Array<string>; // Dataset's identifiers
  height?: number | string; // Height in px
  width?: number | string;
  showDateSelection?: boolean; // Show date selection toolbar
  hideRefresh?: boolean; // Show refresh button when date selection is enabled
  loadConf?: LoadingConfig; // First load default config
  updateFrequency?: number;
  downsampleInterval?: number;
  bins?: {
    start?: number;
    end?: number;
    size?: number;
  };
  loading?: boolean;

  dynamicHeight?: boolean;
  legend?: any;
  xaxisName?: string;
  chartConf?: ILineChartConf;

  datasets?: Array<DatasetSpecification>;
  toggleTracesGrouping?: boolean;
  timeAxisStepSeconds?: number;
  chartDialogOpen?: boolean;
  selectedChartType?: string;
  dateFrom?: Instant;
  dateTo?: Instant;
  xAxisType?: "date" | "category";
  setMinYAxis?: number;
  setMaxYAxis?: number;
  dateAndRefreshButtonsVisible?: boolean;
  productTemplateCreationDate?: Instant;
  currentYAxisRange?: { min: number | undefined; max: number | undefined };
  annotations?: Annotation[];
  hideHeader?: boolean;
  hideLegend?: boolean;
  showFilter?: boolean;
  defaultFilter?: string;
  optionalFilters?: OptionalFilter[];
  filter?: string;
}
export interface DatasetSpecification {
  stringValueMapper?: number;
  overriddenTitle?: string;
  identifier: string;
  line?: ILineConfig;
  range?: Array<number>;
  color?: string;
  bgColor?: string;
}

export interface ILineConfig {
  backoff?: number | Array<number>;
  color?: string;
  dash?: string;
  shape?: string;
  simplify?: boolean;
  smoothing?: number;
  width?: number | null;
}

interface ILineChartConf {
  mode: "lines" | "lines+markers" | "text+lines+markers";
  marker: IMarkerConfig;
}

interface IMarkerConfig {
  size: number;
}

export interface DatasetBarChartRendererProps extends DatasetChartProps, DashboardComponentPropsBase<ReportOptions> {
  dateFormat?: string;
}

export interface DatasetPieProps extends DatasetChartProps, DashboardComponentPropsBase<ReportOptions> {
  charts: Array<{
    aggregationType?: "average" | "sum";
    identifiers: Array<string>;
    chartName?: string;
  }>;
  chartsPerRow?: number;
  getMostRecent?: boolean;
  centerTextConfig?: {
    fontSize?: number;
    decimalPlaces?: number;
  };
  minimumRepresentableValue?: number;
}

export interface ProductTemplateScatter {
  productTemplateId?: number;
  productTemplateRefName?: string;
  templateXAttributeId?: string;
  templateYAttributeId?: string;
  templateSizeAttributeId?: string;
  templateColorAttributeId?: string;
  lastEntryCount?: number;
}

export interface DatasetScatterProps extends DatasetChartProps, IDateRange {
  productTemplate?: ProductTemplateScatter;
  latestEntryCount?: number;
  bubbleChart?: boolean;
  xAxisIdentifier: string;
  yAxisIdentifier: YAxisIdentifier[];
  yAxisIdentifierSelectionType?: YAxisIdentifierSelectionType;
  colorAxisIdentifier?: string;
  sizeAxisIdentifier?: string;

  highlightCount?: number;
  trendLines: boolean;
  discreteColorMode?: boolean;
  discreteValues?: Array<DiscreteValue>;
  highlightedValuesDescription?: string;
  axisConfig?: {
    xRange?: Array<number>;
    yRange?: Array<number>;
  };
  imageConfig?: ImageConfig;

  annotationsConfig?: Array<AnnotationConfig>;
}

export enum YAxisIdentifierSelectionType {
  SINGLE = "Single",
  MULTIPLE = "Multiple",
}

export type DiscreteValue = {
  key: number;
  value: string;
  title: string;
};

export interface YAxisIdentifierWithOverriddenTitle {
  identifier: string;
  overriddenTitle?: string;
  color?: string;
}

export type YAxisIdentifier = YAxisIdentifierWithOverriddenTitle | string;

export type ImageConfig = {
  source: string;
  x: number;
  y: number;
  sizex: number;
  sizey: number;
  opacity: number;
};

export type AnnotationConfig = {
  x: number;
  y: number;
  text: string;

  showarrow?: boolean;
  xanchor?: "auto" | "left" | "center" | "right";
  yanchor?: "auto" | "top" | "middle" | "bottom";
  hovertext?: string;
  font?: {
    color?: string;
    size?: number;
  };
  opacity?: number;
  bordercolor?: string;
  borderpad?: number;
  borderwidth?: number;

  arrowcolor?: string;
  arrowside?: "start" | "end";
  arrowwidth?: number;
  bgcolor?: string;

  textangle?: string;
};

export type DatasetLoadingConf = {
  type?: LineEditorLoadingTypes.TIME;
  lead?: number; // Default 0
  lag: number; // Default 6
  leadUnit: DweenTimeUnit; // Default HOURS
  lagUnit: DweenTimeUnit; // Default HOURS
  alignUnit: DweenTimeUnit | undefined; // Default none/null
};

export class CharState {
  data: Array<Partial<PlotData>> = [];

  axisMap: Map<string, string> = new Map();

  axesConfig: any;
}

export type AxesConfig = {
  [key: string]: Plotly.LayoutAxis;
};

export interface PlotDataEx extends PlotData {
  identifier?: string;
  unit: string;
  currentLineConfig?: {
    color: string;
    width: number;
    dashType: Dash;
    range: {
      min: number | undefined;
      max: number | undefined;
    };
  };
}

export interface IData {
  key: string;
  value: number;
}

export interface IDataSet {
  data: IData[];
  identifier: string;
  title: string;
  unit: string;
  decimalPlaces?: number;
  overriddenTitle?: string;
  stringValueMapper?: number;
  children?: IDataSet[];
  header?: string;
}

export interface ITrace {
  x: number[];
  name?: string;
  color?: string;
}

export interface IHistogram extends ITrace {
  histnorm: "percent";
  type: "histogram";
  opacity: number;
  loadConf?: DatasetLoadingConf; // First load default config
}

export interface IHorizontalBoxPlot extends ITrace {
  type: "box";
}

export interface IPlotChart {
  x: any;
  y: number[];
  type: "scatter";
  mode: "lines";
  name: string;
}

export interface DatasetStringValueMapperDTO {
  id?: number;
  title: string;
  company?: string;
  global: boolean;
  mappings: { [key: string]: string | number };
  mappingIcons: { [key: string]: string };
}

export interface IPIDControllerCommonProps {
  kp: string;
  ki: string;
  kd: string;
  delta: string;
  min: string;
  max: string;
  controlVariable: string;
  setPoint: string;
  manipulatedVariable: string;
  updateFrequency?: number;
  kall: string;
}

export interface IPIDControllerProps extends IPIDControllerCommonProps {
  loadConf?: DatasetLoadingConf;
  updateFrequency?: number;
}

export interface DataForExportLineAndScatter {
  identifier: string;
  name: string;
  type: string;
  mode: string;
  unit: string;
  showlegend: boolean;
  hovertemplate?: string;
  x?: string[];
  y?: number[];
}

export interface DataForExportBar {
  hovertemplate?: string;
  marker?: {
    color: string;
  };
  name: string;
  text: string[];
  type: string;
  unit: string;
  x?: string[];
  y?: number[];
}

export interface PieChartDataForExport {
  hole: number;
  hoverinfo: string;
  hovertemplate: string;
  labels: string[];
  values: number[];
  type: string;
  outsidetextfont?: {
    size: number;
  };
  text: string[];
  textinfo?: string;
  textfont?: {
    size: number;
  };
  marker?: {
    color: string;
  };
}

export interface DataForExportHistogram {
  customdata: number[];
  histnorm: string;
  hovertemplate: string;
  marker: {
    color: string;
  };
  name: string;
  showlegend: boolean;
  type: string;
  unit: string;
  x: number[];
  xbins?: any;
}

export interface IExportCsvDialogProps {
  data: DataForExportLineAndScatter[] | DataForExportBar[] | PieChartDataForExport | DataForExportHistogram[] | Data[];
  dialog: boolean;
  setDialog: React.Dispatch<React.SetStateAction<boolean>>;
  chartType: ChartType;
}

export type Unit = "MONTHS" | "DAYS" | "HOURS" | "MINUTES" | "SECONDS";

export type LagLeadLoadingConfig = {
  type?: LineEditorLoadingTypes.TIME;
  lag: number;
  lead?: number;
  lagUnit: DweenTimeUnit;
  leadUnit: DweenTimeUnit;
  alignUnit: DweenTimeUnit | null;
};

export type ProductEntryLoadingConfig = {
  type: LineEditorLoadingTypes.PRODUCT_ENTRY;
  productTemplateId: string;
  startAttribute: string;
  endAttribute: string | null;
  refName?: string;
};

export interface WeatherComponentDatasetProps {
  isVisible?: boolean;
  dataset: {
    identifier: string;
    line: {
      color: string;
      width: number;
      dash: string;
    };
    overriddenTitle?: string;
  };
}

export interface DateSelectionProps {
  isVisible: boolean;
}

enum TimeOptions {
  DAYS = "DAYS",
  HOURS = "HOURS",
}

export interface WeatherMonitoringProps {
  timeInterval: TimeOptions;
  location?: string;
  temperature?: WeatherComponentDatasetProps;
  feelsLikeTemperature?: WeatherComponentDatasetProps;
  humidity?: WeatherComponentDatasetProps;
  windSpeed?: WeatherComponentDatasetProps;
  windDirection?: WeatherComponentDatasetProps;
  gusture?: WeatherComponentDatasetProps;
  precipitation?: WeatherComponentDatasetProps;
  dateSelection?: DateSelectionProps;
  reportMode?: boolean;
}

export interface WeatherMonitoringTableColumnRowData {
  time: Date;
  temperature: number;
  feelsLikeTemperature: number;
  humidity: number;
  windSpeed: number;
  windDirection: number;
  gusture: number;
  precipitation: number;
  [key: string]: number | Date;
}

export enum Visuals {
  NONE = "none",
  BLINK = "blink",
}

export interface StatusMonitoringIndicator {
  number: number;
  title: string;
  icon: string;
  color: string;
  sound: boolean;
  visual: Visuals;
}

export interface DatasetStatusMonitoringProps {
  identifiers: string[];
  updateFrequency: number;
  updateCheckTime: number;
  indicators: StatusMonitoringIndicator[];
  scale: number;
}

export type LoadingConfig = LagLeadLoadingConfig | ProductEntryLoadingConfig;

export interface Annotation {
  date: Date | null;
  text: string;
  dataset: string | null;
  y: number | null;
}
